import { useEffect, useState } from 'react'
import { Navigate, Outlet, useMatch } from 'react-router'
import { isAuthenticated } from '../../lib/auth.js'
import { applyTheme, getStoredTheme, type Theme } from '../../lib/theme.js'
import { FigmaPluginLightbox } from './FigmaPluginLightbox.js'
import { useFigmaPluginCampaign } from '../../pages/workspace/figmaPluginCampaignStore.js'
import { ProfileRow } from './ProfileRow.js'
import { FavoritesGroup } from './FavoritesGroup.js'
import { SidebarItem } from './SidebarItem.js'
import { TabStrip } from '../workspace/TabStrip.js'
import { Divider } from '../ui/Divider.js'
import {
  IconDefault,
  IconFolders16,
  IconPlugin,
  IconSettings16,
  IconTrash16,
} from '../icons/index.js'

/**
 * WorkspaceAppShell — the flag-ON global shell. DESIGN-HANDOVER.md §8.1, Figma `131:8564`
 * (220px sidebar) shown in situ on `133:15971`.
 *
 * Gated behind `VITE_WORKSPACE_SHELL_ENABLED` per D-CUTOVER-1: flag OFF renders the shipped
 * `AppShell` byte-for-byte and never imports this file. Retiring the flag is Phase 11's job.
 *
 * §8.1's order is fixed and reproduced exactly: profile row (56px) → divider → four 32px nav rows
 * → divider → Favorites → divider → `Figma Plugin` footer in its own 48px block.
 *
 * WHAT THE FOUR NAV ROWS DO, and the decision behind it (D-SHELL-VISIBLE-1, owner, 2026-08-27):
 * Dashboard, Brands, Trash and Admin have NO destination screen drawn anywhere in the evidence.
 * They ship as real routes to an explicit "not built yet" page rather than as invented screens.
 * The owner confirmed on the record that this discharges DESIGN-HANDOVER.md §10 item 25's
 * "Admin/Trash routes and nav entries" and audit condition C5 for the ROWS; the destination
 * CONTENT stays blocked. Note this is a THIRD option — Phase 02B task 2B.1 named only "visibly
 * disabled" or "omitted for first ship" — and a live URL is bookmarkable in a way neither is.
 *
 * NOTHING IS ROLE-GATED, deliberately. §6.7 (how a role-gated item renders for a user lacking the
 * role: hidden / disabled-with-reason / 403) is recorded OPEN, and `users.role` is still plain
 * `text` with no enum — there is nothing to gate against yet. The parked rows leak no capability
 * because they open a placeholder. ⚠ §6.7 MUST be closed BEFORE D-ROLE-1's enum lands, or the
 * Admin row becomes a real leak at that moment. Dated debt, not a dodge.
 *
 * `Divider` (§5.9) renders the three §8.1 separators (items 2, 4, 6) instead of a hand-rolled
 * `border-b` div, its own documented purpose. It has no dark-mode token of its own (index.css's
 * dark-mode decision is deliberately still open — see the `@theme` comment there), so each call
 * passes `className="dark:bg-gray-800"` to reproduce the `dark:border-gray-800` the raw divs
 * carried; a bare `<Divider />` would keep the light-mode `#e6e6e6` line in dark mode instead,
 * a visible regression against D-DARKMODE-1's "every `dark:` class preserved" rule.
 *
 * THE 220PX SIDEBAR IS SCOPED TO THE CAMPAIGN-LIST SHAPE OF ROUTE, NOT GLOBAL (owner, 2026-08-30:
 * "the left menu is not right either — it shows expanded; in the design it appears as icons").
 * Measured, not assumed: `133:15971` (Brand / Campaigns List) contains a 220px `sidebar` instance
 * (`133:15973`) at x=0. `183:24140` (Campaing / File — the per-campaign workspace mounted at
 * `/campaigns/:id`) has NO 220px rail anywhere; its content row (`183:24623`) opens directly with
 * the 52px `Action Sidebar` instance (`183:25004`) at x=0, and `WorkspacePage.tsx` already renders
 * that 52px rail itself (`ActionSidebarRail.tsx`, region 1 of 6) as part of the page, not the
 * shell. So this shell's `<aside>` renders on every route EXCEPT `/campaigns/:id` — the exact
 * shape of the one route the workspace-shell page itself supplies its own rail for. `useMatch`
 * against the literal pattern, not a "has an id" heuristic, so `/campaigns` (the list — which DOES
 * keep the 220px sidebar, per `133:15971`) never matches.
 *
 * TWO THINGS THE FIGMA HAS NO HOME FOR, both kept rather than dropped:
 *   - The theme toggle. Dark mode is a live shipped feature (Sprint 056) and D-DARKMODE-1 keeps
 *     it. The Figma shell simply never draws it, so it stays where it already lives.
 *   - Responsive behaviour. `133:15971` is 1440×810 only; no breakpoint is drawn. Today's shipped
 *     shell hides the rail below `md` and swaps in a compact header, and that behaviour is
 *     carried forward verbatim — dropping working responsive behaviour because a desktop-only
 *     mockup omits it would be a regression dressed up as fidelity.
 *
 * `TabStrip` mounts here (§8.2), ABOVE the sidebar+content row so it spans the full width, with
 * no `current` — this shell wraps every route (`/dashboard`, `/brands`, `/trash`, `/admin`,
 * `/campaigns/:id`) and has no campaign of its own to pass it. `WorkspacePage`, which DOES know
 * the live campaign, registers it into the same shared store via `useRegisterCampaignTab`
 * (`useOpenCampaignTabs.ts`) so this strip picks up the name without either component fetching
 * anything. Epic 48 Phase 02C lift, 2026-08-29.
 *
 * Historical note: Phase 02C shipped `TabStrip` built and verified but NOT mounted here — it
 * took a `current` campaign NAME its own doc said it "deliberately never fetches", and a shell
 * sitting above `<Outlet/>` has no campaign, so a naive lift would have either lost the name or
 * made this shell fetch a campaign it has no business knowing about. The strip kept working
 * where it already was (inside `WorkspacePage`) until the module-level store above made this
 * mount point possible.
 */

/** §8.1 item 3. Icons matched to the set, not guessed — Dashboard genuinely uses the design
 *  system's own `icon / Default` glyph (the dashed circle), which is what `IconDefault` is. */
const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: <IconDefault /> },
  { to: '/brands', label: 'Brands', icon: <IconFolders16 /> },
  { to: '/trash', label: 'Trash', icon: <IconTrash16 /> },
  { to: '/admin', label: 'Admin', icon: <IconSettings16 /> },
] as const

export function WorkspaceAppShell() {
  const [pluginOpen, setPluginOpen] = useState(false)
  // The FIGMA PLUGIN LIGHTBOX'S SECOND STATE — Epic 49 seam-wiring pass. `FigmaPluginLightbox`
  // takes `campaignId`/`campaignName`/`masters`/`ratioThresholds` as caller-supplied props; this
  // shell has none of that data itself and must not learn to fetch a campaign just to have it —
  // `WorkspacePage.tsx` (the ONE place `/campaigns/:id` is actually loaded) already resolves it
  // and registers it into `figmaPluginCampaignStore.ts`, the module-level store built for
  // exactly this shell/page boundary (see that file's header for why context cannot cross it).
  // `null` here — every route this shell wraps EXCEPT `/campaigns/:id`, where `WorkspacePage`
  // never mounts — falls straight through to the dialog's own honest "no campaign in context"
  // default, unchanged from before this wiring existed.
  const figmaPluginCampaign = useFigmaPluginCampaign()
  const [theme] = useState<Theme>(() => getStoredTheme())
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // See the header comment: the 220px sidebar is drawn on `133:15971` (Brand / Campaigns List)
  // but absent from `183:24140` (Campaing / File, the `/campaigns/:id` workspace) — that page
  // supplies its own 52px `ActionSidebarRail` instead. The literal path pattern, not a param
  // presence check, so `/campaigns` itself (the list) is unaffected.
  const isCampaignWorkspaceRoute = useMatch('/campaigns/:id')

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="flex h-screen flex-col bg-surface-white dark:bg-gray-950">
      {/* §8.2 — spans the FULL width, above the sidebar+content row below, not nested inside
          it: `TabStrip` is a direct child of this h-screen column, one row of its own, so the
          sidebar can never push it or make it anything less than edge-to-edge. No `current` —
          see the header comment above. */}
      <TabStrip />

      <div className="flex min-h-0 flex-1">
        {/* 220px, §8.1. Hidden below `md` — see the responsive note above. `<nav>` is the landmark
            a screen-reader user jumps to; the keyboard order is simply DOM order:
            profile → nav rows → Favorites header → brand rows → Figma Plugin.
            SCOPED OFF the `/campaigns/:id` workspace route — see the header comment and the
            `isCampaignWorkspaceRoute` comment above. Every other route keeps it exactly as
            before; this is the only conditional. */}
        {!isCampaignWorkspaceRoute && (
          <aside className="hidden w-[220px] flex-shrink-0 flex-col overflow-y-auto border-r border-border-primary bg-surface-white md:flex dark:border-gray-800 dark:bg-gray-900">
            <ProfileRow />
            <Divider className="dark:bg-gray-800" />

            {/*
              ONE landmark over the whole primary-navigation area, not just the four rows.
              The `<nav>` used to wrap `NAV_ITEMS` only, leaving Favorites — which navigates, by
              writing `?brandId=` — outside it as a plain sibling `<div>`. A screen-reader user
              jumping by landmark would reach four rows and miss the brand list entirely, even
              though the two are one navigation region to anyone looking at the sidebar.
              Found by /design-change-review, 2026-08-28; the contract said "nav + Favorites
              region" and the code had implemented half of it.
            */}
            <nav aria-label="Ana gezinme" className="flex flex-col">
              <div className="flex flex-col gap-xsmall p-medium">
                {NAV_ITEMS.map((item) => (
                  <SidebarItem key={item.to} to={item.to} icon={item.icon} label={item.label} />
                ))}
              </div>
              <Divider className="dark:bg-gray-800" />

              <div className="p-medium">
                <FavoritesGroup />
              </div>
            </nav>
            <Divider className="dark:bg-gray-800" />

            {/* §8.1 item 7. Node `145:18426`: a `p-8` group holding ONE ordinary `sidebar-item` —
                which is where §8.1's "48px block" comes from (8 + 32 + 8), not from a bespoke 48px
                row. Shipped `h-12 px-4`, so the item was 4px from the edge where every other group
                is 8px, and the row itself was not the same shape as its siblings. Corrected
                2026-08-29. This still replaces the floating bottom-right pill the old shell used;
                the Figma puts it here, in the sidebar. */}
            <div className="p-medium">
              <SidebarItem
                icon={<IconPlugin />}
                label="Figma Plugin"
                onClick={() => setPluginOpen(true)}
              />
            </div>
          </aside>
        )}

        {/* `min-h-0` on both: a flex child defaults to `min-height: auto`, so a tall page grows
            the column PAST this row instead of scrolling inside it. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* The below-`md` counterpart to the hidden rail. Not drawn in Figma — carried forward. */}
          <header className="flex items-center gap-small border-b border-border-primary bg-surface-white px-medium py-small md:hidden dark:border-gray-800 dark:bg-gray-900">
            <img src="/favicon.png" alt="Bainner" className="h-8 w-8 object-contain" />
          </header>
          {/* NO PADDING HERE, deliberately — corrected within the hour, 2026-08-29. The 32/24 gutter
              node `133:15974` measures belongs to the campaign LIST page, and putting it on the
              shell pushed the §8.6 workspace's 300px right panel off the right edge: those six
              regions are full-bleed by construction (52px rail + size list + canvas + 300px panel
              already consume the width). A page gutter is a page's value. Pages that want one
              apply it; `WorkspacePage` deliberately does not. */}
          <main className="relative min-h-0 flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Kept: no Figma home, but dark mode is live and D-DARKMODE-1 keeps it.
          The ☀️/🌙 glyphs stay emoji, and that is not icon debt: the accepted 39-icon set has no
          sun / moon / theme glyph (checked against `components/icons/index.ts`, 2026-08-30), which
          follows from the same fact as the line above — the Figma never draws this control, so the
          design system never had a reason to give it an icon. Adding one would be extending an
          accepted design artifact, which is a design decision, not an implementation one. */}
      {/* <button
        type="button"
        onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
        aria-label={theme === 'dark' ? 'Gündüz temasına geç' : 'Karanlık temaya geç'}
        className="fixed bottom-4 left-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-border-primary bg-surface-white text-base shadow-sm hover:bg-surface-tertiary dark:border-gray-700 dark:bg-gray-800"
      >
        <span aria-hidden>{theme === 'dark' ? '☀️' : '🌙'}</span>
      </button>*/}

      {/* `campaignId`/`campaignName`/`masters`/`ratioThresholds` come straight from the store read
          above — `undefined`/`null`/`[]` on every route with no campaign in scope, the real
          values on `/campaigns/:id`. Exactly ONE `FigmaPluginLightbox` in this tree: this is
          still its only mount site, just no longer blind to the campaign the sidebar row that
          opens it happens to sit above.
          ⚠ RE the row itself: the "220px sidebar" note above already records that on
          `/campaigns/:id` this shell's `<aside>` — and with it the "Figma Plugin" row that sets
          `pluginOpen` — does not render at all (a separate, already-accepted decision, not
          something this pass reverses). So today this wiring has no click path on that one exact
          route; it is still correct to carry it regardless, so the moment a reachable trigger
          exists there again this dialog already shows the truth. */}
      <FigmaPluginLightbox
        open={pluginOpen}
        onClose={() => setPluginOpen(false)}
        campaignId={figmaPluginCampaign?.campaignId ?? null}
        campaignName={figmaPluginCampaign?.campaignName ?? null}
        masters={figmaPluginCampaign?.masters ?? []}
        ratioThresholds={figmaPluginCampaign?.ratioThresholds}
        onMastersChanged={figmaPluginCampaign?.onMastersChanged}
      />
    </div>
  )
}
